<template>
  <form
    ref="form"
    :action="submitUrl"
    method="GET"
    role="search"
    :class="allClasses"
    @reset.prevent.stop="onReset"
  >
    <div
      class="search-form__mobile-wrapper"
      :class="{ 'search-form__mobile-wrapper--expanded': mobileWrapperVisible }"
    >
      <div
        class="search-form__search-field"
        data-test-id="search-form-search-field"
        role="combobox"
        aria-autocomplete="list"
        aria-controls="search-form-suggestion-list"
        :aria-expanded="isExpanded"
        v-bind="comboboxAriaAttrs"
      >
        <input
          v-for="(value, key) in hidden"
          :key="key"
          type="hidden"
          :name="key"
          :value="value"
          :data-test-id="`search-form-param-${key}`"
        >
        <div
          class="search-form__input-wrapper"
          :class="{ 'search-form__input-wrapper--highlight': highlightInputWrapper }"
        >
          <EButton
            v-if="mobileWrapperVisible"
            class="search-form__back"
            variant="bare"
            icon="arrow-left"
            icon-class="search-form__reset-icon"
            aria-label="Back"
            data-test-id="search-form-back"
            @click="closeMobileOverlay"
          />
          <input
            ref="input"
            :value="viewInputText"
            :placeholder="placeholder"
            :aria-label="ariaLabel"
            class="search-form__input"
            type="search"
            name="q"
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            :autofocus="hasAutoFocus"
            required
            data-test-id="search-form-input"
            @input="onInput"
            @keydown="onKeydown"
            @focus="onFocus"
            @blur="onBlur"
          >
          <EButton
            class="search-form__reset"
            :class="{ 'search-form__reset--active': isResetVisible }"
            type="reset"
            variant="bare"
            icon="close"
            icon-class="search-form__reset-icon"
            aria-label="Reset"
            data-test-id="search-form-reset"
          />
        </div>
        <EButton
          class="search-form__submit"
          type="submit"
          variant="bare"
          icon="search"
          icon-class="searchf-form__submit-icon"
          :icon-size="variant === 'default' && isTabletUp ? 'm' : 's'"
          aria-label="Submit"
          data-test-id="search-form-submit"
        />
        <div v-if="displaySuggestionsList" class="search-form__suggestions">
          <ESuggestionList
            class="search-form__suggestions-list"
            :input-text="inputText"
            :autocomplete-url="autocompleteUrl"
            :market="market"
            :variant="variant"
            :search-path="submitUrl"
            data-test-id="search-form-suggestions"
            @updateInputText="onSuggestionListInput"
            @triggerSuggestionSearch="onSuggestionListSearch"
            @visibilityChange="onSuggestionListVisibilityChange"
            @selectOption="onSelectOption"
          />
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';
import { getUrlQuery } from '~~/common/js/universal/search';
import { sleep } from '~~/common/js/universal/utils';
import EButton from '~~/common/vue/components/button';
import screen from '~~/common/vue/mixins/screen';
import { toggleScrollLock } from '~~/common/js/client/screen';
import { REQUEST_DEBOUNCE_DELAY, AUTOCOMPLETE_DEBOUNCE_DELAY } from '~~/common/js/constants/events';
import classes, { getModifierClass } from '~~/common/vue/mixins/classes';
import ESuggestionList from './suggestion-list';

export const searchFormVariants = [
  'default',
  'compact',
];

export default {
  name: 'ESearchForm',
  components: {
    EButton,
    ESuggestionList,
  },
  mixins: [classes, screen],
  props: {
    /**
     * Supported variants are `default` and `compact`
     */
    variant: {
      type: String,
      default: 'default',
      validator: (variant) => searchFormVariants.includes(variant),
      cssClass: true,
    },
    ariaLabel: {
      type: String,
      default: '',
    },
    autocompleteUrl: {
      type: String,
      default: '',
    },
    hiddenInputs: {
      type: Object,
      default: () => ({}),
    },
    // eslint-disable-next-line vue/require-default-prop
    market: ESuggestionList.props.market,
    placeholder: {
      type: String,
      default: '',
    },
    action: {
      type: String,
      required: true,
    },
    hasAutoFocus: {
      type: Boolean,
      default: false,
    },
    disableOverlay: {
      type: Boolean,
      default: false,
    },
    /**
     * if enabled, the form will render with a pre-filled query
     * taken from the `q` URL parameter
     */
    prefillQuery: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      highlightInputWrapper: false,
      viewInputText: '',
      inputText: '',
      suggestionsVisible: false,
      mobileWrapperVisible: false,
      resetVisible: false,
      selectedOptionId: '',
    };
  },
  computed: {
    ...mapState('user', ['autoSuggest']),
    allClasses() {
      const allClasses = [...this.classes];
      if (this.suggestionsVisible) {
        allClasses.push(getModifierClass('search-form', 'suggestions-visible'));
      }
      return allClasses;
    },
    query() {
      // $route is not set in the web-extensions
      const query = this.$route?.query.originalQuery || this.$route?.query.q;
      return query?.toString() || '';
    },
    hidden() {
      // TODO: allow params to preserve to come from props
      const verticalParams = [
        'freshness',
        'resolution',
        'videoLength',
        'size',
        'color',
        'imageType',
        'freshness',
        'aspect',
        'license',
      ];
      const params = [
        ...verticalParams,
        'canary',
        'mocked',
        'forceProvider',
        'sfs',
        'sfn',
        'forceMarket',
        'tt',
        'tts',
        'addon',
        'addonversion',
      ];
      const query = this.$route?.query || {};
      return {
        ...getUrlQuery(query, this.$store.state.user, params),
        ...this.hiddenInputs,
      };
    },
    submitUrl() {
      if (!this.$route.hash) {
        return this.action;
      }
      // TODO: make this behaviour customizabile via props
      // TODO: duplicates FILTERS_KEY exported from search/common/js/universal/hash-params
      const filtersKey = 'f';
      const hashParams = new URLSearchParams(this.$route.hash.replace('#', ''));
      const filtersParam = hashParams.get(filtersKey);
      // create a new url params object with only `filters` key
      const hash = new URLSearchParams({ [filtersKey]: filtersParam });
      return `${this.action}#${hash}`;
    },
    displaySuggestionsList() {
      return this.autoSuggest && this.autocompleteUrl;
    },
    isResetVisible() {
      return !!this.inputText && this.resetVisible;
    },
    isExpanded() {
      // Convert to string since boolean attributes is not being rendered by Vue
      // aria-expanded is a required attribute for a11y
      return String(this.suggestionsVisible);
    },
    comboboxAriaAttrs() {
      return this.selectedOptionId ? { 'aria-activedescendant': this.selectedOptionId } : {};
    },
  },
  watch: {
    '$route.query.q': 'onQueryUpdate',
  },
  created() {
    if (this.prefillQuery) {
      this.setInputTexts(this.query);
    }
  },
  beforeMount() {
    this.onInput = debounce(this.onInputChange, AUTOCOMPLETE_DEBOUNCE_DELAY);
  },
  methods: {
    setInputTexts(value) {
      this.viewInputText = value;
      this.inputText = value;
    },
    onInputChange({ target: { value } }) {
      this.setInputTexts(value);
    },
    onKeydown({ key }) {
      if (!this.displaySuggestionsList) {
        return;
      }
      const keyMap = {
        ArrowDown: 'down',
        ArrowUp: 'up',
        Escape: 'esc',
      };
      const name = keyMap[key];
      if (name) {
        this.$emit('onKeyDown', name);
      }
    },
    onFocus() {
      this.$emit('input-focused');
      this.resetVisible = true;
      if (!this.displaySuggestionsList) {
        return;
      }
      // sync text so suggestions correspond to input
      this.inputText = this.viewInputText;
      this.$emit('getSuggestions');
      // Open overlay and lock body scroll in small screens
      this.mobileWrapperVisible = !this.isTabletUp && !this.disableOverlay;
      toggleScrollLock(!this.isTabletUp);
    },
    async onBlur(event) {
      // Safari users who have the "reduced motion" accessibility setting
      // would not be able to use the reset button, because the input field blur handler
      // makes it inactive too early and the click event on the actual reset button never fires.
      // We fix this by delaying the blur handler till the next event loop cycle.
      await sleep();
      this.resetVisible = false;
      if (!this.displaySuggestionsList || this.mobileWrapperVisible) {
        return;
      }
      // Don't close overlay nor hide suggestions on blur
      if (this.isTabletUp) {
        this.hideSuggestions(event);
      }
    },
    hideSuggestions(event) {
      // TODO Refactor this logic to avoid coupling with classname
      const isSuggestionLink = event.relatedTarget ?
        event.relatedTarget.className === 'suggestion-link' :
        false;
      if (!isSuggestionLink) {
        this.$emit('clearSuggestions');
      }
      this.mobileWrapperVisible = false;
      this.suggestionsVisible = false;
      toggleScrollLock(false);
    },
    onSuggestionListInput(value) {
      this.viewInputText = !value ? this.inputText : value;
    },
    onSuggestionListSearch(value) {
      this.onSuggestionListInput(value);
      setTimeout(() => {
        this.$refs.form.submit();
      }, REQUEST_DEBOUNCE_DELAY);
    },
    onSuggestionListVisibilityChange(isVisible) {
      this.suggestionsVisible = isVisible;
    },
    onReset() {
      this.setInputTexts('');
      this.$refs.input.focus();
    },
    closeMobileOverlay(event) {
      // we want to reset the input text so it corresponds to the shown results
      this.viewInputText = this.query;
      this.hideSuggestions(event);
    },
    onSelectOption(optionId) {
      this.selectedOptionId = optionId;
    },
    onQueryUpdate() {
      window?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      this.setInputTexts(this.query);
      this.$refs.input.focus();
      this.highlightInputWrapper = true;
      setTimeout(() => {
        this.highlightInputWrapper = false;
      }, 500);
    },
  },
};
</script>

<style lang="scss" scoped>
.search-form__search-field {
  display: flex;
  z-index: $z-index-l;
  margin: 0 auto;
  background-color: var(--color-background-primary);
  position: relative;
  border-radius: $border-radius-1l;
  justify-content: space-around;
  align-items: center;
  text-align: left;
  height: $space-3l;
}

.search-form--variant-default {
  @include tablet {
    .search-form__search-field {
      border-radius: $border-radius-2l;
      height: 60px;
    }

    .search-form__input-wrapper {
      border-radius: $border-radius-2l 0 0 $border-radius-2l;
      padding-left: $space-m;
    }

    .search-form__input {
      font-size: $font-2l;
    }

    &.search-form--suggestions-visible {
      .search-form__input-wrapper {
        border-top-left-radius: 30px;
        border-bottom-left-radius: 0;
      }

      .search-form__search-field {
        border-bottom-left-radius: 0;
      }
    }
  }
}

.search-form--variant-compact {
  .search-form__search-field {
    @include elevation(1);
  }
}

.search-form__input-wrapper {
  border-radius: $border-radius-1l 0 0 $border-radius-1l;
  border: solid $border-width var(--color-form-border-default);
  border-right: none;
  flex: 1 1 auto;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-left: $space-s;
  transition: background-color $timing-s $easing;

  &.search-form__input-wrapper--highlight {
    background-color: var(--color-highlight-primary);
  }

  @include tablet {
    padding-left: $space-m;
  }
}

.search-form__input {
  font-size: $font-l;
  border: none;
  height: 100%;
  width: 100%;
  color: var(--color-text-primary);
  background-color: transparent;
  padding: 0;

  &::placeholder {
    color: var(--color-text-secondary);
    opacity: 1;
    text-overflow: ellipsis;
  }

  &::-ms-clear {
    display: none;
  }

  &:focus {
    outline: none;
  }

  &:required {
    box-shadow: none;
  }

  &:invalid {
    box-shadow: none;
  }
}

.search-form__reset {
  margin-right: $space-m;
  visibility: hidden;
}

.search-form__reset,
.search-form__reset:focus {
  height: $space-2l;
  min-width: $space-2l;
  margin-right: $space-1s;
}

.search-form .search-form__reset {
  @include desktop {
    visibility: hidden;
  }
}

.search-form:focus-within:valid .search-form__reset,
.search-form:hover:valid .search-form__reset {
  @include desktop {
    visibility: visible;
  }
}

.search-form__reset--active {
  visibility: visible;
}

.search-form__submit {
  background-color: transparent;
  color: var(--color-button-content-tertiary);
  border: $border-width solid var(--color-form-border-default);
  border-left: 0;
  border-radius: 0 $border-radius-full $border-radius-full 0;
  height: 100%;
  min-width: $space-4l;
  transition: all $timing-2s $easing;
}

// keeps submit button the same size as the adjacent input
$outline-difference: 6px;
// not replicated! Needs this in order to not break when it's focused by a mouse interaction
.search-form__submit:focus {
  height: calc(100% - #{$outline-difference});
  outline: solid 2px var(--color-focused);
  outline-offset: 1px;
}

.search-form__submit:hover {
  color: var(--color-button-content-primary);
  background-color: var(--color-button-background-primary-hover);
  border-color: var(--color-button-background-primary-hover);
}

.search-form:focus-within .search-form__submit {
  color: var(--color-button-content-primary);
  background-color: var(--color-button-background-primary);
  border-color: var(--color-button-background-primary);

  &:hover {
    background-color: var(--color-button-background-primary-hover);
    border-color: var(--color-button-background-primary-hover);
  }
}

// TODO: implement this for all buttons of all sizes in the Button component
$tapable-button-margin: $space-2s;

.search-form .button::before {
  content: '';
  min-height: $tappable-area-size-mobile;
  min-width: $tappable-area-size-mobile;
  position: absolute;
  margin: -$tapable-button-margin;
}
</style>

<style lang="scss">
.search-form--suggestions-visible {
  .search-form__input-wrapper,
  .search-form__search-field {
    border-top-left-radius: $border-radius-1l;
    border-bottom-left-radius: 0;
  }
}

.search-form__mobile-wrapper--expanded {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: var(--color-background-primary);
  z-index: $z-index-1l;
  padding: $space-1s $space-m 0;

  .search-form__input-wrapper,
  .search-form__search-field {
    border-bottom-left-radius: $border-radius-1l;
  }

  .search-form__input-wrapper {
    padding-left: 0;
  }

  .search-form {
    @include elevation(1);

    margin-bottom: $space-1s;
    border-radius: $border-radius-1l;
    transition: box-shadow;
  }

  .suggestion-list {
    position: absolute;
    background: none;
    border: none;
    box-shadow: none;

    // Occupy the whole width:
    left: - $space-m;
    right: - $space-m;
    width: 100vw;
  }

  animation: e-slide-up;
  animation-duration: $timing-2s;
  animation-timing-function: ease-out;
}

@keyframes e-slide-up {
  0% {
    // The height of header icons, plus top and bottom margins
    padding-top: $space-3l + $space-1s + $space-1s;
  }

  100% {
    padding-top: $space-1s;
  }
}
</style>
